import { useState } from 'react';
import { useBell, useMagicBellEvent, INotification } from '@magicbell/react-headless';
import {
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTheme,
  Badge,
  Button,
  Tooltip,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { bgBlur } from 'src/utils/cssStyles';
import { handleQueryClientInvalidation, processNotification } from './utils';
import HeadlessNotificationsTabs from './HeadlessNotificationsTabs';
import Markdown from 'src/components/markdown';
import { IconButtonAnimate } from 'src/components/animate';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const SPACING = 2.5;

const showToast = (variant: 'default' | 'error' | 'success' | 'warning' | 'info') => {
  switch (variant) {
    case 'success':
      return toast.success;
    case 'error':
      return toast.error;
    case 'warning':
      return toast.warning;
    case 'info':
      return toast.info;
    case 'default':
      return toast;
    default:
      return toast;
  }
};

function HeadlessNotificationsDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const queryClient = useQueryClient();

  useMagicBellEvent('notifications.new', (data) => {
    const event = data as INotification;
    const processedNotification = processNotification(theme, event);
    const { title, content, color, variant, actionUrl, actionText } = processedNotification;

    handleQueryClientInvalidation(processedNotification, queryClient);

    // if (actionUrl) {
    //   snackbarOptions.action = (key) => (
    //     <Stack flexDirection="row" gap={0.25}>
    //       <Button
    //         size="small"
    //         variant="text"
    //         color={color}
    //         component="a"
    //         href={actionUrl}
    //         target="_blank"
    //         rel="noopener noreferrer"
    //       >
    //         {actionText || 'View Details'}
    //       </Button>
    //       <Button variant="text" size="small" color="secondary" onClick={() => closeSnackbar(key)}>
    //         Dismiss
    //       </Button>
    //     </Stack>
    //   );
    // }

    showToast(variant)(title, {
      description: () => {
        return (
          <Markdown
            sx={{
              '& p': { margin: 0 },
              '& ul': { fontSize: 'inherit', lineHeight: '1.5' },
              '& ul > li': { lineHeight: '1.5' },
            }}
          >
            {content || ''}
          </Markdown>
        );
      },
      action: actionUrl ? (
        <Button
          size="small"
          variant="text"
          color={color}
          component="a"
          href={actionUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ flexShrink: 0 }}
        >
          {actionText || 'View Details'}
        </Button>
      ) : (
        <></>
      ),
    });
  });

  const notificationStore = useBell({ storeId: 'inbox' });

  return (
    <>
      <Tooltip
        title="Notifications"
        arrow
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        }}
      >
        <Stack>
          <IconButtonAnimate
            onClick={() => {
              setIsOpen(true);
            }}
            sx={{ width: 36, height: 36 }}
          >
            <Badge badgeContent={notificationStore?.unreadCount} color="error">
              <Iconify color={theme.palette.text.secondary} icon="eva:bell-fill" width={20} />
            </Badge>
          </IconButtonAnimate>
        </Stack>
      </Tooltip>
      <Drawer
        anchor="right"
        onClose={() => setIsOpen(false)}
        open={isOpen}
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: 'transparent',
            },
          },
        }}
        PaperProps={{
          sx: {
            ...bgBlur({
              color: theme.palette.background.default,
              opacity: 0.9,
              blur: 20,
              imgUrl: ['/assets/cyan-blur.png', '/assets/red-blur.png'],
            }),
            width: '100%',
            maxWidth: 420,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: 2, pr: 1, pl: SPACING }}
        >
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Notifications
          </Typography>

          <IconButton onClick={() => setIsOpen(false)}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>

        <Divider />

        <HeadlessNotificationsTabs isOpen={isOpen} />
      </Drawer>
    </>
  );
}

export default HeadlessNotificationsDrawer;
